import { forwardRef, Fragment, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { maybe, rd, RemoteData } from '@passionware/monads';
import { Table } from 'v5/design-system/Table';
import {
  SortableTableHeader,
  useSortableTableHeader
} from 'v5/patterns/table/SortableTableHeader';
import { Skeleton } from 'v5/design-system/Skeleton';
import { WithSorter } from 'v5/api/_common/query/queryUtils';
import { resolveEntityListEmptyContent } from './resolveEntityListEmptyContent';
import { OutOfRangePageEmptyState } from './EntityEmptyState';
import { Sorter } from 'v5/api/_common/query/sorters/Sorter';
import { Nullable } from 'v5/platform/typescript';
import { cn } from 'v5/platform/dom/cn';
import { Button } from 'v5/design-system/Button';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import type { EntityListRowErrorBoundaryLabels } from './EntityListRowErrorBoundary';
import { EntityListRowCellErrorBoundary } from './EntityListRowCellErrorBoundary';
import { EntityListRowRenderErrorPanel } from './EntityListRowRenderErrorPanel';
import { getDimmedContainerClassName } from 'v4/shared/components/forms/input/primitvies/DimmedContainer';
import {
  selectionState,
  SelectionState
} from 'v5/platform/lang/SelectionState';
import { SelectionLayout } from 'v5/patterns/page-layout/SelectionLayout';
import { Checkbox } from 'v5/design-system/Checkbox';
import { renderInlineError } from 'v5/patterns/feedback/ErrorAlert';
import { StopPropagation } from 'v5/platform/react/StopPropagation';
import type { TableColumnDefinition } from 'v5/patterns/table/tableColumns';
import type { ValidatedRow } from 'v5/platform/row-level-validation';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import { EntityListRowErrorBoundary } from './EntityListRowErrorBoundary';
import {
  asEntityForUnsafeRender,
  ValidatedRowInvalidRenderPanel
} from './ValidatedRowInvalidRenderPanel';
import { EntityListRowValidationIndicator } from './EntityListRowValidationIndicator';
import { entityListInvalidRowSimpleClassName } from './entityListInvalidRowChrome';
import { useAdvancedValidationRowUi } from './useAdvancedValidationRowUi';
import type { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import type { WithServices } from 'v5/platform/ts/services';
import {
  ChevronRight,
  ListChevronsDownUp,
  ListChevronsUpDown,
  ListRestart
} from 'lucide-react';
import {
  SortableEntityTableRowReorderRoot,
  SortableEntityTableSortableRow,
  type SortableEntityTableRowReorderConfig
} from './SortableEntityTableRowReorder';

import {
  EntityTableDragHandle,
  EntityTableLockedHandle
} from './EntityTableDragHandle';
import {
  chunkAdjacentGroups,
  resolveAdjacentGroupCellRole
} from './adjacentColumnGroup';

export type { SortableEntityTableRowReorderConfig };
export {
  EntityTableDragHandle,
  EntityTableDragOverlay,
  EntityTableLockedHandle
} from './EntityTableDragHandle';
export { EntityEmptyState, OutOfRangePageEmptyState } from './EntityEmptyState';
export type {
  EntityEmptyStateProps,
  OutOfRangePageEmptyStateProps
} from './EntityEmptyState';

export type SortableEntityTableColumn<
  TEntity,
  Q,
  TSortField extends string
> = Omit<TableColumnDefinition<TEntity, Q>, 'field'> & {
  field: NoInfer<TSortField> | string;
};

/**
 * Invokes `render()` in this component's render so throws are handled by
 * {@link EntityListRowErrorBoundary}. Calling `render()` directly in an ancestor
 * (e.g. inside `rd.journey().map`) runs inside RemoteData's try/catch and becomes
 * a RemoteData `MappingError` instead.
 */
function DeferredTableCellRender({ render }: { render: () => ReactNode }) {
  return <>{render()}</>;
}

DeferredTableCellRender.displayName = 'DeferredTableCellRender';

function SortableEntityTableValidationHeaderCell({
  srLabel
}: {
  srLabel: string;
}) {
  return (
    <Table.HeaderCell className="w-10 border-r-0! px-0 align-middle">
      <span className="sr-only">{srLabel}</span>
    </Table.HeaderCell>
  );
}

function SortableEntityTableValidationBodyCell({
  indicator
}: {
  indicator?: ReactNode;
}) {
  return (
    <Table.Cell className="w-10 border-r-0! px-0 align-middle">
      <span className="inline-flex size-6 items-center justify-center">
        {indicator}
      </span>
    </Table.Cell>
  );
}

/** Content-sized: icon menus stay compact; text actions (e.g. callsheet share) can grow. */
const ROW_ACTIONS_COLUMN_CLASS = 'min-w-16 w-[1%] text-right whitespace-nowrap';

export interface SortableEntityTableProps<
  TEntity extends { id: string | number },
  Q extends WithSorter<TSortField>,
  TSortField extends string
> extends Omit<React.ComponentPropsWithRef<typeof Table.Container>, 'children'>,
    WithServices<[WithFeatureService]> {
  // Data (RemoteData) — each row is a {@link ValidatedRow} (lift with `rd.map(data, rowLevelValidation.mapToValidatedRows)` when not from ResourceService).
  data: RemoteData<ValidatedRow<TEntity>[]>;

  // Query
  query: Q;
  onQueryChange: (query: Q) => void;

  // Column configuration
  columns: Array<SortableEntityTableColumn<TEntity, Q, TSortField>>;

  // Query utils
  queryUtils: {
    setSort: (query: Q, sort: Nullable<Sorter<TSortField>>) => Q;
  };

  // Key extractor — defaults to `entity.id` when omitted
  getKey?: (entity: TEntity) => string | number;

  // Expanded row configuration (optional; uses SelectionState for open/closed rows)
  rowExpansion?: SelectionState<string | number>;
  onRowExpansionChange?: (state: SelectionState<string | number>) => void;
  renderExpandedRow?: (entity: TEntity) => ReactNode;

  // Row actions (only valid rows are interactive)
  onRowClick?: (entity: TEntity) => void;
  /** When set with `onRowClick`, only matching rows get click/hover affordances. */
  isRowClickable?: (entity: TEntity) => boolean;
  /** Extra props merged onto each valid data row (e.g. HTML5 drag-and-drop). */
  getRowProps?: (
    entity: TEntity
  ) => React.HTMLAttributes<HTMLTableRowElement> | undefined;
  rowActions?: (entity: TEntity) => ReactNode;

  // Empty state
  emptyState?: ReactNode;
  /**
   * Total matching rows for the current list query (not just this page).
   * When set and the URL page is past the last page, the table shows
   * {@link OutOfRangePageEmptyState} instead of `emptyState`.
   */
  paginationTotalCount?: number;

  // Loading skeleton configuration (optional, has defaults)
  loadingSkeleton?: {
    rowCount?: number; // Default: 5
    renderSkeleton?: () => ReactNode; // Custom skeleton renderer
  };

  selection?: SelectionState<TEntity['id']>;
  onSelectionChange?: (selection: SelectionState<TEntity['id']>) => void;
  /**
   * When true with `selection` + `onSelectionChange`, keep the selection
   * column width but render an empty spacer (no checkboxes). Use to align
   * a read-only table above an editable one that has selection.
   */
  selectionSpacer?: boolean;
  /** Extra classes on the inner `<table>` (e.g. `table-fixed`). */
  tableClassName?: string;
  /**
   * Optional `<colgroup>` rendered as the first child of the table. Use with
   * `table-fixed` so stacked tables (e.g. read-only above editable) share
   * identical column widths.
   */
  colGroup?: ReactNode;

  /**
   * Optional @dnd-kit row reorder with DragOverlay.
   * When set, a dedicated leading drag column is rendered (before expand and
   * selection). Defaults: grip handle (Space + arrows via KeyboardSensor),
   * lock icon for `isItemDisabled` rows, and overlay from `getItemLabel`.
   * Keep the config with `enabled: false` (instead of `undefined`) to keep the
   * column visible while reorder is temporarily unavailable (e.g. sorted or
   * filtered list).
   */
  rowReorder?: SortableEntityTableRowReorderConfig<TEntity['id']>;
}

const SortableEntityTableComponent = forwardRef<
  HTMLDivElement,
  SortableEntityTableProps<any, any, any>
>(function SortableEntityTableComponent<
  TEntity extends { id: string | number },
  Q extends WithSorter<TSortField>,
  TSortField extends string
>(
  {
    data,
    query,
    onQueryChange,
    columns,
    queryUtils,
    getKey,
    onRowClick,
    isRowClickable,
    getRowProps,
    rowActions,
    emptyState,
    paginationTotalCount,
    loadingSkeleton,
    className,
    selection,
    onSelectionChange,
    selectionSpacer = false,
    tableClassName,
    colGroup,
    rowExpansion: rowExpansionProp,
    onRowExpansionChange,
    renderExpandedRow,
    rowReorder,
    services,
    ...rest
  }: SortableEntityTableProps<TEntity, Q, TSortField>,
  ref
) {
  const advancedValidationRowUi = useAdvancedValidationRowUi(
    services.featureService
  );
  const { t } = useTranslation('patterns', {
    keyPrefix: 'SortableEntityTable'
  });
  const { handleSortChange } = useSortableTableHeader(query.sort, sorter =>
    onQueryChange(queryUtils.setSort(query, sorter))
  );

  const rowExpansion = rowExpansionProp ?? selectionState.selectNone();
  const showExpandColumn = !!(onRowExpansionChange && renderExpandedRow);
  const showDragColumn = rowReorder != null;
  const resolveKey = getKey ?? ((entity: TEntity) => entity.id);

  const renderDragHeaderCell = () => {
    const canClearSortToReorder = query.sort != null;

    return (
      <Table.HeaderCell className="border-r-0! w-10 min-w-10 pr-0 normal-case">
        {canClearSortToReorder ? (
          <SimpleTooltip title={t('clearSortToReorder')}>
            <Button
              variant="headless"
              size="xs"
              layout="icon"
              aria-label={t('clearSortToReorder')}
              onClick={e => {
                e.stopPropagation();
                onQueryChange(queryUtils.setSort(query, null));
              }}
            >
              <ListRestart className="size-4" aria-hidden />
            </Button>
          </SimpleTooltip>
        ) : (
          // In-flow box so the column cannot collapse when only sr-only text
          // would otherwise be present (stacked read-only tables).
          <span className="inline-flex size-7 items-center justify-center">
            <span className="sr-only">{t('reorderSrOnly')}</span>
          </span>
        )}
      </Table.HeaderCell>
    );
  };

  const renderDragSkeletonCell = () => (
    <Table.Cell className="border-r-0! w-10 min-w-10 pr-0">
      <Skeleton className="h-6 w-6" />
    </Table.Cell>
  );

  const renderDragHandleCell = (id: string | number) => (
    <Table.Cell className="w-10 min-w-10 align-middle border-r-0! pr-0">
      <span className="inline-flex size-7 items-center justify-center">
        {rowReorder?.renderHandleCell?.(id as TEntity['id']) ??
          (rowReorder?.isItemDisabled?.(id as TEntity['id']) ? (
            <EntityTableLockedHandle aria-label={t('locked')} />
          ) : (
            <EntityTableDragHandle aria-label={t('dragToReorder')} />
          ))}
      </span>
    </Table.Cell>
  );

  const isExpandedKey = (key: string | number): boolean =>
    selectionState.isSelected(rowExpansion, key);

  const renderExpandHeaderCell = (pageItems: TEntity[]) => {
    const keys = pageItems.map(resolveKey);
    const headerExpanded = selectionState.isSelectAll(rowExpansion);

    return (
      <Table.HeaderCell className="border-r-0! w-10 pr-0 normal-case">
        {keys.length > 0 ? (
          <SimpleTooltip
            title={headerExpanded ? t('collapseAllRows') : t('expandAllRows')}
          >
            <Button
              variant="headless"
              size="xs"
              layout="icon"
              aria-label={
                headerExpanded ? t('collapseAllRows') : t('expandAllRows')
              }
              aria-expanded={headerExpanded}
              data-expand-target="inline"
              onClick={e => {
                e.stopPropagation();
                onRowExpansionChange?.(
                  selectionState.toggleSelectAll(rowExpansion)
                );
              }}
            >
              {headerExpanded ? (
                <ListChevronsDownUp className="size-4" aria-hidden />
              ) : (
                <ListChevronsUpDown className="size-4" aria-hidden />
              )}
            </Button>
          </SimpleTooltip>
        ) : null}
      </Table.HeaderCell>
    );
  };

  const renderExpandSkeletonCell = () => (
    <Table.Cell className="border-r-0! w-10 pr-0">
      <Skeleton className="h-6 w-6" />
    </Table.Cell>
  );

  const renderExpandToggleCell = (key: string | number) => {
    const isExpandedRow = isExpandedKey(key);
    return (
      <Table.Cell
        className="w-10 align-middle border-r-0! pr-0 cursor-pointer"
        onClick={e => {
          e.stopPropagation();
          onRowExpansionChange?.(selectionState.toggle(rowExpansion, key));
        }}
      >
        <Button
          variant="headless"
          size="xs"
          layout="icon"
          aria-label={isExpandedRow ? t('collapseRow') : t('expandRow')}
          aria-expanded={isExpandedRow}
          data-expand-target="inline"
        >
          <ChevronRight
            className={cn(
              'size-4',
              'transition-transform duration-200',
              isExpandedRow && 'transform-[rotate(90deg)]'
            )}
            aria-hidden
          />
        </Button>
      </Table.Cell>
    );
  };

  const rowCount = loadingSkeleton?.rowCount ?? 5;
  const renderSkeleton = loadingSkeleton?.renderSkeleton;

  return rd
    .journey(data)
    .wait(
      (renderSkeleton ? (
        <>{renderSkeleton()}</>
      ) : (
        <Table.Container
          ref={ref}
          className={cn(
            'flex-1 flex flex-col h-full with-styled-scrollbars',
            className
          )}
          {...rest}
        >
          <Table.Root>
            <Table.Header className="sticky top-0 z-10 bg-bg-default after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:z-10 after:h-px after:bg-border-default [&_tr]:border-b-0 [&_th]:border-b-0">
              <Table.Row>
                {showDragColumn && renderDragHeaderCell()}
                {showExpandColumn && renderExpandHeaderCell([])}
                {selection && onSelectionChange && (
                  <Table.HeaderCell className="border-r-0 pr-0 w-12">
                    <Skeleton className="h-6 w-6" />
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell className="border-r-0">
                  <Skeleton className="h-6 w-32" />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {Array.from({ length: rowCount }, (_, index) => (
                <Table.Row key={index} className="border-b-0">
                  {showDragColumn && renderDragSkeletonCell()}
                  {showExpandColumn && renderExpandSkeletonCell()}
                  {selection && onSelectionChange && (
                    <Table.Cell className="border-r-0 pr-0 w-12">
                      <Skeleton className="h-6 w-6" />
                    </Table.Cell>
                  )}
                  <Table.Cell className="border-r-0">
                    <Skeleton className="h-10 w-full" />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Table.Container>
      )) as React.ReactElement
    )
    .catch(renderInlineError)
    .map(items => {
      if (items.length === 0) {
        return resolveEntityListEmptyContent({
          emptyState,
          query,
          totalCount: paginationTotalCount,
          onQueryChange,
          outOfRangeEmptyState: goToLastPage => (
            <OutOfRangePageEmptyState onGoToLastPage={goToLastPage} />
          )
        });
      }

      return (
        <SortableEntityTableNonEmpty<TEntity, Q, TSortField>
          items={items}
          data={data}
          isPlaceholder={rd.isPlaceholderData(data)}
          tableRef={ref}
          className={className}
          tableContainerRest={rest}
          columns={columns}
          rowActions={rowActions}
          query={query}
          handleSortChange={handleSortChange}
          selection={selection}
          onSelectionChange={onSelectionChange}
          selectionSpacer={selectionSpacer}
          tableClassName={tableClassName}
          colGroup={colGroup}
          onRowClick={onRowClick}
          isRowClickable={isRowClickable}
          getRowProps={getRowProps}
          resolveKey={resolveKey}
          showDragColumn={showDragColumn}
          renderDragHeaderCell={renderDragHeaderCell}
          renderDragHandleCell={renderDragHandleCell}
          showExpandColumn={showExpandColumn}
          rowExpansion={rowExpansion}
          onRowExpansionChange={onRowExpansionChange}
          renderExpandedRow={renderExpandedRow}
          renderExpandHeaderCell={renderExpandHeaderCell}
          renderExpandToggleCell={renderExpandToggleCell}
          isExpandedKey={isExpandedKey}
          advancedValidationRowUi={advancedValidationRowUi}
          rowReorder={rowReorder}
        />
      );
    });
});

function SortableEntityTableInvalidRowUnsafeCells<
  TEntity extends { id: string | number },
  Q extends WithSorter<TSortField>,
  TSortField extends string
>({
  row,
  selection,
  onSelectionChange,
  selectItemAriaLabel,
  selectionSpacerOnly,
  renderCrashLabels,
  showDragColumn,
  renderDragHandleCell,
  showExpandColumn,
  renderExpandToggleCell,
  expansionKey,
  columns,
  rowActions,
  query,
  showValidationColumn,
  validationIndicator
}: {
  row: Extract<ValidatedRow<TEntity>, { isValid: false }>;
  selection: SortableEntityTableProps<TEntity, Q, TSortField>['selection'];
  onSelectionChange: SortableEntityTableProps<
    TEntity,
    Q,
    TSortField
  >['onSelectionChange'];
  selectItemAriaLabel: string;
  selectionSpacerOnly?: boolean;
  renderCrashLabels: EntityListRowErrorBoundaryLabels;
  showDragColumn: boolean;
  renderDragHandleCell: (id: string | number) => ReactNode;
  showExpandColumn: boolean;
  renderExpandToggleCell: (key: string | number) => ReactNode;
  expansionKey: string | number;
  columns: SortableEntityTableProps<TEntity, Q, TSortField>['columns'];
  rowActions: SortableEntityTableProps<TEntity, Q, TSortField>['rowActions'];
  query: Q;
  showValidationColumn: boolean;
  validationIndicator?: ReactNode;
}) {
  const selectionRowId = row.id;
  const unsafeEntity = asEntityForUnsafeRender<TEntity>(row.rawItem);

  return (
    <>
      {showDragColumn && renderDragHandleCell(row.id)}
      {showExpandColumn && renderExpandToggleCell(expansionKey)}
      {selection && onSelectionChange && (
        <Table.Cell
          data-entity-list-selection-affordance=""
          onClick={
            selectionSpacerOnly
              ? undefined
              : (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onSelectionChange(
                    selectionState.toggle(selection, selectionRowId)
                  );
                }
          }
          className="border-r-0! pr-0 w-12 min-w-12 align-middle"
          aria-hidden={selectionSpacerOnly}
        >
          {selectionSpacerOnly ? (
            <span className="inline-flex size-6 items-center justify-center">
              <span className="inline-block size-4 shrink-0" />
            </span>
          ) : (
            <span className="inline-flex size-6 items-center justify-center">
              <Checkbox
                variant="primaryAlt"
                checked={maybe.mapOrElse(
                  selection,
                  s => selectionState.isSelected(s, selectionRowId),
                  false
                )}
                onCheckedChange={() => {
                  onSelectionChange(
                    selectionState.toggle(selection, selectionRowId)
                  );
                }}
                aria-label={selectItemAriaLabel}
              />
            </span>
          )}
        </Table.Cell>
      )}
      {showValidationColumn && (
        <SortableEntityTableValidationBodyCell
          indicator={validationIndicator}
        />
      )}
      {columns.map(column => (
        <Table.Cell key={column.field} className={column.className}>
          <EntityListRowCellErrorBoundary labels={renderCrashLabels}>
            <DeferredTableCellRender
              render={() => column.render(unsafeEntity, query)}
            />
          </EntityListRowCellErrorBoundary>
        </Table.Cell>
      ))}
      {rowActions && (
        <StopPropagation>
          <Table.Cell className={ROW_ACTIONS_COLUMN_CLASS}>
            <EntityListRowCellErrorBoundary labels={renderCrashLabels}>
              <DeferredTableCellRender
                render={() => rowActions(unsafeEntity)}
              />
            </EntityListRowCellErrorBoundary>
          </Table.Cell>
        </StopPropagation>
      )}
    </>
  );
}

SortableEntityTableInvalidRowUnsafeCells.displayName =
  'SortableEntityTableInvalidRowUnsafeCells';

function SortableEntityTableInvalidRow<
  TEntity extends { id: string | number },
  Q extends WithSorter<TSortField>,
  TSortField extends string
>({
  row,
  colSpan,
  panelColSpan,
  selection,
  onSelectionChange,
  selectItemAriaLabel,
  selectionSpacerOnly,
  renderCrashLabels,
  columns,
  rowActions,
  query,
  onRowClick,
  isRowClickable,
  advancedValidationRowUi,
  showDragColumn,
  renderDragHandleCell,
  showExpandColumn,
  renderExpandToggleCell,
  showValidationColumn,
  resolveKey,
  isExpandedKey,
  renderExpandedRow
}: {
  row: Extract<ValidatedRow<TEntity>, { isValid: false }>;
  colSpan: number;
  panelColSpan: number;
  showValidationColumn: boolean;
  resolveKey: (entity: TEntity) => string | number;
  isExpandedKey: (key: string | number) => boolean;
  renderExpandedRow?: (entity: TEntity) => ReactNode;
  selection: SortableEntityTableProps<TEntity, Q, TSortField>['selection'];
  onSelectionChange: SortableEntityTableProps<
    TEntity,
    Q,
    TSortField
  >['onSelectionChange'];
  selectItemAriaLabel: string;
  selectionSpacerOnly?: boolean;
  renderCrashLabels: EntityListRowErrorBoundaryLabels;
  columns: SortableEntityTableProps<TEntity, Q, TSortField>['columns'];
  rowActions: SortableEntityTableProps<TEntity, Q, TSortField>['rowActions'];
  query: Q;
  onRowClick: SortableEntityTableProps<TEntity, Q, TSortField>['onRowClick'];
  isRowClickable: SortableEntityTableProps<
    TEntity,
    Q,
    TSortField
  >['isRowClickable'];
  advancedValidationRowUi: boolean;
  showDragColumn: boolean;
  renderDragHandleCell: (id: string | number) => ReactNode;
  showExpandColumn: boolean;
  renderExpandToggleCell: (key: string | number) => ReactNode;
}) {
  const [unsafePreviewEnabled, setUnsafePreviewEnabled] = useState(false);
  const selectionRowId = row.id;
  const invalidAriaSelected = maybe.mapOrElse(
    selection,
    s => !!selectionState.isSelected(s, selectionRowId),
    false
  );
  if (!advancedValidationRowUi && row.rawItem !== undefined) {
    const unsafeEntity = asEntityForUnsafeRender<TEntity>(row.rawItem);
    const expansionKey = resolveKey(unsafeEntity);
    const canClickRow =
      Boolean(onRowClick) && (isRowClickable?.(unsafeEntity) ?? true);

    return (
      <>
        <Table.Row
          className={cn(
            entityListInvalidRowSimpleClassName,
            canClickRow && 'cursor-pointer'
          )}
          data-item-id={String(row.id)}
          data-row-id={String(row.id)}
          aria-selected={invalidAriaSelected}
          onClick={canClickRow ? () => onRowClick?.(unsafeEntity) : undefined}
        >
          <EntityListRowErrorBoundary
            labels={renderCrashLabels}
            renderFallback={({ error, reset }) => (
              <Table.Cell colSpan={colSpan} className="py-3">
                <EntityListRowRenderErrorPanel
                  title={renderCrashLabels.title}
                  message={error.message}
                  tryAgainLabel={renderCrashLabels.tryAgain}
                  onTryAgain={reset}
                />
              </Table.Cell>
            )}
          >
            <SortableEntityTableInvalidRowUnsafeCells
              row={row}
              selection={selection}
              onSelectionChange={onSelectionChange}
              selectItemAriaLabel={selectItemAriaLabel}
              renderCrashLabels={renderCrashLabels}
              showDragColumn={showDragColumn}
              renderDragHandleCell={renderDragHandleCell}
              showExpandColumn={showExpandColumn}
              renderExpandToggleCell={renderExpandToggleCell}
              expansionKey={expansionKey}
              columns={columns}
              rowActions={rowActions}
              query={query}
              showValidationColumn={showValidationColumn}
              validationIndicator={
                <EntityListRowValidationIndicator
                  error={row.error}
                  rawItem={row.rawItem}
                />
              }
            />
          </EntityListRowErrorBoundary>
        </Table.Row>
        {showExpandColumn &&
          isExpandedKey(expansionKey) &&
          renderExpandedRow && (
            <Table.Row data-item-id={String(row.id)}>
              <Table.Cell colSpan={colSpan} className="px-0">
                <EntityListRowErrorBoundary labels={renderCrashLabels}>
                  {renderExpandedRow(unsafeEntity)}
                </EntityListRowErrorBoundary>
              </Table.Cell>
            </Table.Row>
          )}
      </>
    );
  }

  return (
    <>
      <Table.Row
        className={cn(
          'bg-bg-danger/25',
          Boolean(
            onRowClick &&
              row.rawItem &&
              (isRowClickable?.(
                asEntityForUnsafeRender<TEntity>(row.rawItem)
              ) ??
                true)
          ) && 'relative cursor-pointer'
        )}
        data-item-id={String(row.id)}
        aria-selected={invalidAriaSelected}
        onClick={
          onRowClick &&
          row.rawItem &&
          (isRowClickable?.(asEntityForUnsafeRender<TEntity>(row.rawItem)) ??
            true)
            ? () => onRowClick?.(asEntityForUnsafeRender<TEntity>(row.rawItem))
            : undefined
        }
      >
        {showDragColumn && renderDragHandleCell(row.id)}
        {selection && onSelectionChange && (
          <Table.Cell
            data-entity-list-selection-affordance=""
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onSelectionChange(
                selectionState.toggle(selection, selectionRowId)
              );
            }}
            className="border-r-0! pr-0 w-12 min-w-12 cursor-pointer align-middle"
          >
            <span className="inline-flex size-6 items-center justify-center">
              <Checkbox
                variant="primaryAlt"
                checked={maybe.mapOrElse(
                  selection,
                  s => selectionState.isSelected(s, selectionRowId),
                  false
                )}
                onCheckedChange={() => {
                  onSelectionChange(
                    selectionState.toggle(selection, selectionRowId)
                  );
                }}
                aria-label={selectItemAriaLabel}
              />
            </span>
          </Table.Cell>
        )}
        <Table.Cell colSpan={panelColSpan} className="py-3">
          <ValidatedRowInvalidRenderPanel<TEntity>
            error={row.error}
            rawItem={row.rawItem}
            renderItem={() => null}
            separatePreviewSurface
            unsafePreview={unsafePreviewEnabled}
            onUnsafePreviewChange={setUnsafePreviewEnabled}
          />
        </Table.Cell>
      </Table.Row>
      {unsafePreviewEnabled && row.rawItem !== undefined && (
        <Table.Row
          className={cn(
            'bg-bg-danger/25',
            Boolean(
              onRowClick &&
                (isRowClickable?.(
                  asEntityForUnsafeRender<TEntity>(row.rawItem)
                ) ??
                  true)
            ) && 'relative cursor-pointer'
          )}
          data-item-id={String(row.id)}
          data-row-id={String(row.id)}
          aria-selected={invalidAriaSelected}
          onClick={
            onRowClick &&
            (isRowClickable?.(asEntityForUnsafeRender<TEntity>(row.rawItem)) ??
              true)
              ? () =>
                  onRowClick?.(asEntityForUnsafeRender<TEntity>(row.rawItem))
              : undefined
          }
        >
          <EntityListRowErrorBoundary
            labels={renderCrashLabels}
            renderFallback={({ error, reset }) => (
              <Table.Cell colSpan={colSpan} className="py-3">
                <EntityListRowRenderErrorPanel
                  title={renderCrashLabels.title}
                  message={error.message}
                  tryAgainLabel={renderCrashLabels.tryAgain}
                  onTryAgain={reset}
                />
              </Table.Cell>
            )}
          >
            <SortableEntityTableInvalidRowUnsafeCells
              row={row}
              selection={selection}
              onSelectionChange={onSelectionChange}
              selectItemAriaLabel={selectItemAriaLabel}
              selectionSpacerOnly={selectionSpacerOnly}
              renderCrashLabels={renderCrashLabels}
              showDragColumn={showDragColumn}
              renderDragHandleCell={renderDragHandleCell}
              showExpandColumn={showExpandColumn}
              renderExpandToggleCell={renderExpandToggleCell}
              expansionKey={
                row.rawItem !== undefined
                  ? resolveKey(asEntityForUnsafeRender<TEntity>(row.rawItem))
                  : row.id
              }
              columns={columns}
              rowActions={rowActions}
              query={query}
              showValidationColumn={showValidationColumn}
              validationIndicator={
                showValidationColumn ? (
                  <EntityListRowValidationIndicator
                    error={row.error}
                    rawItem={row.rawItem}
                  />
                ) : undefined
              }
            />
          </EntityListRowErrorBoundary>
        </Table.Row>
      )}
    </>
  );
}

SortableEntityTableInvalidRow.displayName = 'SortableEntityTableInvalidRow';

function SortableEntityTableNonEmpty<
  TEntity extends { id: string | number },
  Q extends WithSorter<TSortField>,
  TSortField extends string
>({
  items,
  data,
  isPlaceholder,
  tableRef,
  className,
  tableContainerRest,
  columns,
  rowActions,
  query,
  handleSortChange,
  selection,
  onSelectionChange,
  onRowClick,
  isRowClickable,
  getRowProps,
  resolveKey,
  showDragColumn,
  renderDragHeaderCell,
  renderDragHandleCell,
  showExpandColumn,
  rowExpansion,
  onRowExpansionChange,
  renderExpandedRow,
  renderExpandHeaderCell,
  renderExpandToggleCell,
  isExpandedKey,
  advancedValidationRowUi,
  rowReorder,
  selectionSpacer = false,
  tableClassName,
  colGroup
}: {
  items: ValidatedRow<TEntity>[];
  data: RemoteData<ValidatedRow<TEntity>[]>;
  isPlaceholder: boolean;
  tableRef: React.Ref<HTMLDivElement>;
  className: string | undefined;
  tableContainerRest: Omit<
    React.ComponentPropsWithRef<typeof Table.Container>,
    'children' | 'ref' | 'className'
  >;
  columns: SortableEntityTableProps<TEntity, Q, TSortField>['columns'];
  rowActions: SortableEntityTableProps<TEntity, Q, TSortField>['rowActions'];
  query: Q;
  handleSortChange: (field: TSortField) => void;
  selection: SortableEntityTableProps<TEntity, Q, TSortField>['selection'];
  onSelectionChange: SortableEntityTableProps<
    TEntity,
    Q,
    TSortField
  >['onSelectionChange'];
  selectionSpacer?: boolean;
  tableClassName?: string;
  colGroup?: ReactNode;
  onRowClick: SortableEntityTableProps<TEntity, Q, TSortField>['onRowClick'];
  isRowClickable: SortableEntityTableProps<
    TEntity,
    Q,
    TSortField
  >['isRowClickable'];
  getRowProps: SortableEntityTableProps<TEntity, Q, TSortField>['getRowProps'];
  resolveKey: (entity: TEntity) => string | number;
  showDragColumn: boolean;
  renderDragHeaderCell: () => ReactNode;
  renderDragHandleCell: (id: string | number) => ReactNode;
  showExpandColumn: boolean;
  rowExpansion: SelectionState<string | number>;
  onRowExpansionChange?: (state: SelectionState<string | number>) => void;
  renderExpandedRow?: (entity: TEntity) => ReactNode;
  renderExpandHeaderCell: (pageItems: TEntity[]) => ReactNode;
  renderExpandToggleCell: (key: string | number) => ReactNode;
  isExpandedKey: (key: string | number) => boolean;
  advancedValidationRowUi: boolean;
  rowReorder?: SortableEntityTableRowReorderConfig<TEntity['id']>;
}) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'SortableEntityTable'
  });
  const { t: tRow } = useTranslation('patterns', {
    keyPrefix: 'EntityListRow'
  });
  const renderCrashLabels: EntityListRowErrorBoundaryLabels = {
    title: tRow('renderCrashed'),
    tryAgain: tRow('renderTryAgain')
  };
  const allRows = rd.tryGet(data) ?? [];
  const allSelectableIds = rowLevelValidation.referencedIds(allRows);
  const allSelectableCount = allSelectableIds.length;
  const validEntities = items
    .filter(
      (row): row is Extract<ValidatedRow<TEntity>, { isValid: true }> =>
        row.isValid
    )
    .map(row => row.data);
  const showValidationColumn =
    !advancedValidationRowUi && items.some(item => !item.isValid);
  const rowReorderEnabled = Boolean(rowReorder?.enabled);
  // Invalid rows have no backing entity, so they must not participate in DnD.
  const reorderItemIds = useMemo(
    () => items.filter(row => row.isValid).map(row => row.id as TEntity['id']),
    [items]
  );

  // Rowspan cells break native row hocus; one tbody per group + Tailwind hover.
  const groupingKeyFn = columns.find(
    column => column.getGroupingKey
  )?.getGroupingKey;
  const bodyChunks = groupingKeyFn
    ? chunkAdjacentGroups(items, groupingKeyFn)
    : [
        {
          startIndex: 0,
          items,
          isMultiRowGroup: false
        }
      ];

  const colSpan =
    (showDragColumn ? 1 : 0) +
    (showExpandColumn ? 1 : 0) +
    (selection && onSelectionChange ? 1 : 0) +
    (showValidationColumn ? 1 : 0) +
    columns.length +
    (rowActions ? 1 : 0);

  const renderBodyRow = (row: (typeof items)[number], rowIndex: number) => {
    const rowKey = row.id;
    if (!row.isValid) {
      const panelColSpan =
        colSpan -
        (showDragColumn ? 1 : 0) -
        (selection && onSelectionChange ? 1 : 0);
      return (
        <SortableEntityTableInvalidRow<TEntity, Q, TSortField>
          key={String(row.id)}
          row={row}
          colSpan={colSpan}
          panelColSpan={panelColSpan}
          selection={selection}
          onSelectionChange={onSelectionChange}
          selectItemAriaLabel={t('selectItem', {
            key: String(rowKey)
          })}
          selectionSpacerOnly={selectionSpacer}
          renderCrashLabels={renderCrashLabels}
          columns={columns}
          rowActions={rowActions}
          query={query}
          onRowClick={onRowClick}
          isRowClickable={isRowClickable}
          advancedValidationRowUi={advancedValidationRowUi}
          showDragColumn={showDragColumn}
          renderDragHandleCell={renderDragHandleCell}
          showExpandColumn={showExpandColumn}
          renderExpandToggleCell={renderExpandToggleCell}
          showValidationColumn={showValidationColumn}
          resolveKey={resolveKey}
          isExpandedKey={isExpandedKey}
          renderExpandedRow={renderExpandedRow}
        />
      );
    }
    const entity = row.data;
    const selectionRowId = row.id;
    const expansionKey = resolveKey(entity);
    const canClickRow =
      Boolean(onRowClick) && (isRowClickable?.(entity) ?? true);
    const {
      className: rowPropsClassName,
      onClick: rowPropsOnClick,
      ...restRowProps
    } = getRowProps?.(entity) ?? {};
    const rowClassName = cn(
      canClickRow ? 'cursor-pointer relative' : undefined,
      rowPropsClassName
    );
    const rowOnClick =
      canClickRow || rowPropsOnClick
        ? (event: React.MouseEvent<HTMLTableRowElement>) => {
            rowPropsOnClick?.(event);
            if (canClickRow && !event.defaultPrevented) {
              onRowClick?.(entity);
            }
          }
        : undefined;
    const rowAriaSelected = maybe.mapOrElse(
      selection,
      selection => !!selectionState.isSelected(selection, selectionRowId),
      false
    );
    const rowCells = (
      <EntityListRowErrorBoundary
        labels={renderCrashLabels}
        renderFallback={({ error, reset }) => (
          <Table.Cell colSpan={colSpan} className="py-3">
            <EntityListRowRenderErrorPanel
              title={renderCrashLabels.title}
              message={error.message}
              tryAgainLabel={renderCrashLabels.tryAgain}
              onTryAgain={reset}
            />
          </Table.Cell>
        )}
      >
        {showDragColumn && renderDragHandleCell(row.id)}
        {showExpandColumn && renderExpandToggleCell(expansionKey)}
        {selection && onSelectionChange && (
          <Table.Cell
            data-entity-list-selection-affordance=""
            onClick={
              selectionSpacer
                ? undefined
                : (e: React.MouseEvent) => {
                    e.stopPropagation();
                    onSelectionChange(
                      selectionState.toggle(selection, selectionRowId)
                    );
                  }
            }
            className={cn(
              'border-r-0! pr-0 w-12 min-w-12 align-middle',
              !selectionSpacer && 'cursor-pointer'
            )}
            aria-hidden={selectionSpacer}
          >
            {selectionSpacer ? (
              <span className="inline-flex size-6 items-center justify-center">
                <span className="inline-block size-4 shrink-0" />
              </span>
            ) : (
              <span className="inline-flex size-6 items-center justify-center">
                <Checkbox
                  variant="primaryAlt"
                  checked={maybe.mapOrElse(
                    selection,
                    selection =>
                      selectionState.isSelected(selection, selectionRowId),
                    false
                  )}
                  onCheckedChange={() => {
                    onSelectionChange(
                      selectionState.toggle(selection, selectionRowId)
                    );
                  }}
                  aria-label={t('selectItem', {
                    key: String(rowKey)
                  })}
                />
              </span>
            )}
          </Table.Cell>
        )}
        {showValidationColumn && <SortableEntityTableValidationBodyCell />}
        {columns.map(column => {
          const role = column.getGroupingKey
            ? resolveAdjacentGroupCellRole(
                items,
                rowIndex,
                column.getGroupingKey
              )
            : { type: 'render' as const, rowSpan: 1, entity };
          if (role.type === 'omit') return null;
          return (
            <Table.Cell
              key={column.field}
              className={cn(column.className, role.rowSpan > 1 && 'align-top')}
              rowSpan={role.rowSpan > 1 ? role.rowSpan : undefined}
            >
              <EntityListRowCellErrorBoundary labels={renderCrashLabels}>
                <DeferredTableCellRender
                  render={() => column.render(role.entity, query)}
                />
              </EntityListRowCellErrorBoundary>
            </Table.Cell>
          );
        })}
        {rowActions && (
          <StopPropagation>
            <Table.Cell className={ROW_ACTIONS_COLUMN_CLASS}>
              <EntityListRowCellErrorBoundary labels={renderCrashLabels}>
                <DeferredTableCellRender render={() => rowActions(entity)} />
              </EntityListRowCellErrorBoundary>
            </Table.Cell>
          </StopPropagation>
        )}
      </EntityListRowErrorBoundary>
    );
    return (
      <Fragment key={String(rowKey)}>
        {rowReorderEnabled ? (
          <SortableEntityTableSortableRow
            id={row.id}
            {...restRowProps}
            onClick={rowOnClick}
            className={rowClassName}
            aria-selected={rowAriaSelected}
          >
            {rowCells}
          </SortableEntityTableSortableRow>
        ) : (
          <Table.Row
            {...restRowProps}
            onClick={rowOnClick}
            className={rowClassName}
            data-item-id={String(row.id)}
            aria-selected={rowAriaSelected}
          >
            {rowCells}
          </Table.Row>
        )}
        {showExpandColumn &&
          isExpandedKey(expansionKey) &&
          renderExpandedRow && (
            <Table.Row data-item-id={String(row.id)}>
              <Table.Cell colSpan={colSpan} className="px-0">
                {renderExpandedRow(entity)}
              </Table.Cell>
            </Table.Row>
          )}
      </Fragment>
    );
  };

  const body = bodyChunks.map(chunk => (
    <Table.Body
      key={String(chunk.items[0]?.id ?? chunk.startIndex)}
      className={
        chunk.isMultiRowGroup
          ? cn(
              // Rowspan cell hovered/focused → wash all rows in this group.
              // Bare hocus:[&>tr] would light the whole group on any child hover.
              'has-[[rowspan]:is(:hover,:focus-within)]:[&>tr]:bg-fg-default/3',
              // Any hocus in group → opaque paint on spanning cell (no stack with tr hocus).
              'hocus:[&>tr>[rowspan]]:relative hocus:[&>tr>[rowspan]]:z-1',
              'hocus:[&>tr>[rowspan]]:bg-[color-mix(in_srgb,var(--color-fg-default)_3%,var(--color-bg-default))]'
            )
          : undefined
      }
    >
      {chunk.items.map((row, localIndex) =>
        renderBodyRow(row, chunk.startIndex + localIndex)
      )}
    </Table.Body>
  ));

  const table = (
    <Table.Container
      ref={tableRef}
      className={cn(
        'flex-1 flex flex-col h-full with-styled-scrollbars',
        getDimmedContainerClassName(isPlaceholder),
        className
      )}
      {...tableContainerRest}
    >
      <Table.Root className={tableClassName}>
        {colGroup}
        <Table.Header
          data-entity-list-no-marquee=""
          className="sticky top-0 z-10 bg-bg-default after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:z-10 after:h-px after:bg-border-default [&_tr]:border-b-0 [&_th]:border-b-0"
        >
          <Table.Row>
            {showDragColumn && renderDragHeaderCell()}
            {showExpandColumn && renderExpandHeaderCell(validEntities)}
            {selection && onSelectionChange && (
              <Table.HeaderCell
                data-entity-list-selection-affordance=""
                className={cn(
                  'border-r-0! pr-0 w-12 min-w-12 align-middle',
                  !selectionSpacer && 'cursor-pointer'
                )}
                aria-hidden={selectionSpacer}
                onClick={
                  selectionSpacer
                    ? undefined
                    : () => {
                        onSelectionChange(
                          selectionState.toggleSelectAll(selection)
                        );
                      }
                }
              >
                {selectionSpacer ? (
                  <span className="inline-flex size-6 items-center justify-center">
                    <span className="inline-block size-4 shrink-0" />
                  </span>
                ) : (
                  <span className="inline-flex size-6 items-center justify-center">
                    <Checkbox
                      variant="primaryAlt"
                      checked={
                        selectionState.isSelectAll(selection)
                          ? true
                          : selectionState.isPartiallySelected(
                                selection,
                                allSelectableCount
                              )
                            ? 'indeterminate'
                            : false
                      }
                      onCheckedChange={() => {
                        onSelectionChange(
                          selectionState.toggleSelectAll(selection)
                        );
                      }}
                      aria-label={t('selectAllItems')}
                    />
                  </span>
                )}
              </Table.HeaderCell>
            )}
            {showValidationColumn && (
              <SortableEntityTableValidationHeaderCell
                srLabel={t('validationIssueSrOnly')}
              />
            )}
            {columns.map(column => {
              if (column.sortable && column.field) {
                return (
                  <SortableTableHeader
                    key={column.field}
                    field={column.field as TSortField}
                    sort={query.sort}
                    onSortChange={handleSortChange}
                    className={column.headerClassName}
                  >
                    {column.label}
                  </SortableTableHeader>
                );
              }
              return (
                <Table.HeaderCell
                  key={column.field}
                  className={column.headerClassName}
                >
                  {column.label}
                </Table.HeaderCell>
              );
            })}
            {rowActions && (
              <Table.HeaderCell className={ROW_ACTIONS_COLUMN_CLASS}>
                <span className="sr-only">{t('actionsSrOnly')}</span>
              </Table.HeaderCell>
            )}
          </Table.Row>
        </Table.Header>
        {body}
      </Table.Root>
    </Table.Container>
  );

  return (
    <SortableEntityTableRowReorderRoot
      itemIds={reorderItemIds}
      rowReorder={rowReorder}
    >
      {selection && onSelectionChange ? (
        <SelectionLayout
          selectedIds={selectionState
            .getSelectedIds(selection, allSelectableIds)
            .map(String)}
          onSelectedIdsChange={ids => {
            const allData = rd.tryGet(data) ?? [];
            const selectedIds = new Set(ids);
            const matchingIds = allData
              .filter(item => selectedIds.has(String(item.id)))
              .map(item => item.id);
            return onSelectionChange(selectionState.selectSome(matchingIds));
          }}
        >
          {table}
        </SelectionLayout>
      ) : (
        table
      )}
    </SortableEntityTableRowReorderRoot>
  );
}

SortableEntityTableComponent.displayName = 'SortableEntityTable';

export const SortableEntityTable = SortableEntityTableComponent as <
  TEntity extends { id: string | number },
  Q extends WithSorter<TSortField>,
  TSortField extends string
>(
  props: SortableEntityTableProps<TEntity, Q, TSortField> & {
    ref?: React.Ref<HTMLDivElement>;
  }
) => React.ReactElement;
